import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'

/**
 * Theme control.
 *
 * Three states, not two. `system` is a real choice and the default — a
 * first-time visitor gets their OS preference — and it is distinct from having
 * explicitly chosen light on a dark-mode machine. Collapsing the two would
 * make a user's explicit light choice silently flip when they enable dark mode
 * at the OS level, which is exactly the bug tokens.css's `:not([data-theme])`
 * guard exists to prevent.
 */
export type UiThemePreference = 'light' | 'dark' | 'system'

/** What is actually on screen once `system` has been resolved. */
export type UiResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'fcms.theme'

const preference = ref<UiThemePreference>('system')
const systemDark = ref(false)
let installed = false

function isPreference(value: unknown): value is UiThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * Writes the attribute tokens.css keys off.
 *
 * `system` REMOVES the attribute rather than writing the resolved value. That
 * is the whole point: with no attribute, `prefers-color-scheme` decides live,
 * so a user who flips their OS theme mid-session sees the app follow without a
 * reload and without us listening for it.
 */
function applyToDocument(value: UiThemePreference): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (value === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', value)
  }
  // Native form controls, scrollbars, and the browser chrome read this.
  root.style.colorScheme = value === 'system' ? 'light dark' : value
}

function readStored(): UiThemePreference | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isPreference(raw) ? raw : null
  } catch {
    // Safari private mode throws on localStorage access. A theme is not worth
    // breaking the app over; fall back to `system`.
    return null
  }
}

function writeStored(value: UiThemePreference): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    /* see readStored */
  }
}

function install(): void {
  if (installed) return
  installed = true

  const stored = readStored()
  if (stored) preference.value = stored

  if (typeof matchMedia === 'function') {
    const list = matchMedia('(prefers-color-scheme: dark)')
    systemDark.value = list.matches
    const onChange = (event: MediaQueryListEvent) => {
      systemDark.value = event.matches
    }
    if (typeof list.addEventListener === 'function') list.addEventListener('change', onChange)
    else list.addListener(onChange)
  }

  applyToDocument(preference.value)
  // `flush: 'sync'` deliberately. The attribute must land before the next
  // paint, or the page renders one frame in the old palette — a white flash on
  // the way into dark mode. It also means a caller can set the theme and read
  // the resulting attribute in the same tick, which is what the tests do.
  watch(preference, applyToDocument, { flush: 'sync' })

  // Another tab changed the preference. Financial staff routinely run the back
  // office in several tabs; a theme that only changes in one is jarring.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY && isPreference(event.newValue)) {
        preference.value = event.newValue
      }
    })
  }
}

export interface UseThemeReturn {
  /** The user's choice, including `system`. Writable. */
  theme: Ref<UiThemePreference>
  /** What is on screen right now: `light` or `dark`, never `system`. */
  resolved: ComputedRef<UiResolvedTheme>
  /** True when the resolved theme is dark. */
  isDark: ComputedRef<boolean>
  setTheme: (value: UiThemePreference) => void
  /** Cycles light → dark → system. The order a toggle button should follow. */
  cycleTheme: () => void
}

/**
 * The server-side half of "persisted per account" is the caller's: the app
 * fetches the profile's saved preference and calls `setTheme` with it on
 * login, and PATCHes the profile from a `watch` on `theme`. This composable
 * deliberately does not know about the API, because a design-system primitive
 * that issues HTTP requests is a primitive that cannot be tested or reused.
 */
export function useTheme(): UseThemeReturn {
  install()

  const resolved = computed<UiResolvedTheme>(() => {
    if (preference.value === 'system') return systemDark.value ? 'dark' : 'light'
    return preference.value
  })

  const setTheme = (value: UiThemePreference) => {
    preference.value = value
    writeStored(value)
  }

  const cycleTheme = () => {
    const order: UiThemePreference[] = ['light', 'dark', 'system']
    const next = order[(order.indexOf(preference.value) + 1) % order.length] ?? 'system'
    setTheme(next)
  }

  const writable = computed<UiThemePreference>({
    get: () => preference.value,
    set: setTheme,
  })

  return { theme: writable, resolved, isDark: computed(() => resolved.value === 'dark'), setTheme, cycleTheme }
}

/**
 * Emitted by `useTheme` consumers that need to react to a theme change from
 * outside Vue's reactivity — the uPlot chart, which must be torn down and
 * redrawn with the new concrete colours. Exported so `UiChart` can observe the
 * same source of truth rather than polling `getComputedStyle`.
 */
export function onThemeChange(handler: (theme: UiResolvedTheme) => void): () => void {
  install()
  const stop = watch(
    () => (preference.value === 'system' ? (systemDark.value ? 'dark' : 'light') : preference.value),
    (value) => handler(value as UiResolvedTheme),
  )
  return stop
}

/** Test-only: forgets the installed listeners and the stored preference. */
export function __resetTheme(): void {
  installed = false
  preference.value = 'system'
  systemDark.value = false
}
